import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { MakeOfferDialog } from "@/components/negotiations";
import {
  Phone,
  Mail,
  Lock,
  Loader2,
  CheckCircle,
  IndianRupee,
  MessageCircle,
  Send,
  AlertCircle,
  ArrowLeft,
  MessageSquare
} from "lucide-react";

export function CargoBookingSidebar({ listing, bookingOpen, setBookingOpen, user, children }) {
  const [offerDialogOpen, setOfferDialogOpen] = useState(false);

  return (
    <div className="lg:col-span-1">
      <div className="sticky top-24 border border-border p-6 space-y-6">
        <div>
          <p className="text-sm text-muted-foreground mb-1">Price</p>
          <div className="flex items-baseline gap-2">
            <span className="text-4xl font-black">{listing.price_per_kg}</span>
            <span className="text-muted-foreground">INR/kg</span>
          </div>
        </div>
        
        <div className="pt-4 border-t border-border">
          <p className="text-sm text-muted-foreground mb-1">Available Capacity</p>
          <p className="text-2xl font-bold">{listing.available_capacity} kg</p>
        </div>

        {/* Contact Info - Locked */}
        <div className="pt-4 border-t border-border">
          <div className="flex items-center gap-2 text-muted-foreground mb-3">
            <Lock className="w-4 h-4" />
            <span className="text-sm">Partner contact hidden until payment</span>
          </div>
          <div className="space-y-2 opacity-50 blur-sm select-none">
            <div className="flex items-center gap-2 text-sm">
              <Phone className="w-4 h-4" />
              <span>+91 98XXXXXX00</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Mail className="w-4 h-4" />
              <span>partner@email.com</span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="space-y-3">
          <Dialog open={bookingOpen} onOpenChange={setBookingOpen}>
            <DialogTrigger asChild>
              <Button 
                size="lg" 
                className="w-full rounded-none btn-hover bg-accent text-accent-foreground hover:bg-accent/90"
                data-testid="book-now-btn"
              >
                Book Space Now
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              {children}
            </DialogContent>
          </Dialog>

          {/* Make an Offer Button */}
          {user && user.user_type !== "partner" && (
            <Button 
              size="lg" 
              variant="outline"
              className="w-full rounded-none"
              onClick={() => setOfferDialogOpen(true)}
              data-testid="make-offer-btn"
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              Make an Offer
            </Button>
          )}
        </div>

        <p className="text-xs text-center text-muted-foreground">
          2% platform fee on booking value
        </p>
      </div>

      {/* Offer Dialog */}
      <MakeOfferDialog
        open={offerDialogOpen}
        onOpenChange={setOfferDialogOpen}
        listing={listing}
        listingType="cargo"
        onSuccess={() => {
          // Could refresh or show negotiation status
        }}
      />
    </div>
  );
}

export function CargoBookingFormStep({
  listing,
  bookingForm,
  setBookingForm,
  totalAmount,
  platformFee,
  bookingLoading,
  onSubmit
}) {
  return (
    <>
      <DialogHeader>
        <DialogTitle>Book Cargo Space</DialogTitle>
      </DialogHeader>
      <form onSubmit={onSubmit} className="space-y-4 py-4">
        <div className="space-y-2">
          <Label htmlFor="quantity">Cargo Weight (kg) *</Label>
          <Input
            id="quantity"
            type="number"
            max={listing.available_capacity}
            placeholder={`Max: ${listing.available_capacity} kg`}
            value={bookingForm.quantity}
            onChange={(e) => setBookingForm({ ...bookingForm, quantity: e.target.value })}
            required
            className="rounded-none"
            data-testid="booking-quantity-input"
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="shipper_name">Your Name *</Label>
          <Input
            id="shipper_name"
            placeholder="Full name"
            value={bookingForm.shipper_name}
            onChange={(e) => setBookingForm({ ...bookingForm, shipper_name: e.target.value })}
            required
            className="rounded-none"
            data-testid="booking-name-input"
          />
        </div>

        {bookingForm.quantity > 0 && (
          <div className="pt-4 border-t border-border space-y-2">
            <div className="flex justify-between text-sm">
              <span>Subtotal ({bookingForm.quantity} kg × ₹{listing.price_per_kg})</span>
              <span>₹{totalAmount.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm text-muted-foreground">
              <span>Platform fee (2%)</span>
              <span>₹{platformFee.toFixed(2)}</span>
            </div>
            <div className="flex justify-between font-bold pt-2 border-t">
              <span>Total</span>
              <span>₹{(totalAmount + platformFee).toFixed(2)}</span>
            </div>
          </div>
        )}
        
        <Button 
          type="submit" 
          className="w-full rounded-none btn-hover bg-accent text-accent-foreground hover:bg-accent/90"
          disabled={bookingLoading}
          data-testid="continue-to-payment-btn"
        >
          {bookingLoading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            "Continue to Payment"
          )}
        </Button>
      </form>
    </>
  );
}

export function CargoPaymentStep({ currentBooking, bookingLoading, onPayment }) {
  return (
    <>
      <DialogHeader>
        <DialogTitle>Complete Payment</DialogTitle>
      </DialogHeader>
      <div className="space-y-6 py-4">
        <div className="p-4 bg-secondary/30 border border-border">
          <div className="flex justify-between mb-2">
            <span>Booking Amount</span>
            <span className="font-bold">₹{currentBooking.total_amount.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-sm text-muted-foreground">
            <span>Platform Fee (2%)</span>
            <span>₹{currentBooking.platform_fee.toFixed(2)}</span>
          </div>
          <div className="flex justify-between font-bold pt-2 mt-2 border-t border-border">
            <span>Total Payable</span>
            <span>₹{(currentBooking.total_amount).toFixed(2)}</span>
          </div>
        </div>

        <div className="flex items-start gap-3 p-4 bg-amber-500/10 border border-amber-500/20">
          <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-700">
            Partner contact details will be revealed after successful payment. You can also chat with them directly.
          </p>
        </div>

        <Button 
          onClick={onPayment}
          className="w-full rounded-none btn-hover bg-emerald-600 hover:bg-emerald-700"
          disabled={bookingLoading}
          data-testid="pay-now-btn"
        >
          {bookingLoading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <>
              <IndianRupee className="w-4 h-4 mr-1" />
              Pay ₹{currentBooking.total_amount.toFixed(2)}
            </>
          )}
        </Button>
        <p className="text-xs text-center text-muted-foreground">
          Secure payment via Razorpay
        </p>
      </div>
    </>
  );
}

export function CargoSuccessStep({ currentBooking, onChat, onDashboard }) {
  return (
    <>
      <DialogHeader>
        <DialogTitle>Booking Confirmed!</DialogTitle>
      </DialogHeader>
      <div className="space-y-6 py-4">
        <div className="text-center">
          <CheckCircle className="w-16 h-16 mx-auto text-emerald-500 mb-4" />
          <p className="text-muted-foreground mb-4">
            Payment successful! Here are the partner's contact details:
          </p>
        </div>
        
        <div className="space-y-3 p-4 border border-border bg-secondary/30">
          <div className="flex items-center gap-3">
            <Phone className="w-5 h-5 text-muted-foreground" />
            <a href={`tel:${currentBooking.partner_contact_phone}`} className="font-medium hover:text-accent">
              {currentBooking.partner_contact_phone || "Not provided"}
            </a>
          </div>
          <div className="flex items-center gap-3">
            <Mail className="w-5 h-5 text-muted-foreground" />
            <a href={`mailto:${currentBooking.partner_contact_email}`} className="font-medium hover:text-accent">
              {currentBooking.partner_contact_email || "Not provided"}
            </a>
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-3">
          <Button 
            variant="outline"
            className="rounded-none" 
            onClick={onChat}
            data-testid="open-chat-btn"
          >
            <MessageCircle className="w-4 h-4 mr-2" />
            Chat
          </Button>
          <Button 
            className="rounded-none" 
            onClick={onDashboard}
            data-testid="go-to-dashboard-btn"
          >
            Dashboard
          </Button>
        </div>
      </div>
    </>
  );
}

export function CargoChatStep({ messages, user, newMessage, setNewMessage, sendingMessage, onSend, onBack, messagesEndRef }) {
  return (
    <>
      <DialogHeader>
        <DialogTitle>Chat with Partner</DialogTitle>
      </DialogHeader>
      <div className="space-y-4 py-4">
        <div className="h-64 overflow-y-auto border border-border p-4 space-y-3 bg-secondary/10">
          {messages.length === 0 ? (
            <p className="text-center text-muted-foreground text-sm py-8">
              No messages yet. Start the conversation!
            </p>
          ) : (
            messages.map((msg) => (
              <div 
                key={msg.id}
                className={`flex ${msg.sender_id === user?.user_id ? "justify-end" : "justify-start"}`}
              >
                <div className={`max-w-[80%] p-3 ${
                  msg.sender_id === user?.user_id 
                    ? "bg-accent text-accent-foreground" 
                    : "bg-secondary"
                }`}>
                  <p className="text-xs opacity-70 mb-1">{msg.sender_name}</p>
                  <p className="text-sm">{msg.content}</p>
                </div>
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>

        <div className="flex items-start gap-3 p-3 bg-amber-500/10 border border-amber-500/20 text-xs">
          <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0" />
          <span className="text-amber-700">
            Contact sharing is disabled in chat. Use the contact details above.
          </span>
        </div>

        <form onSubmit={onSend} className="flex gap-2">
          <Input
            placeholder="Type a message..."
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            className="rounded-none"
            data-testid="chat-input"
          />
          <Button 
            type="submit" 
            size="icon" 
            className="rounded-none"
            disabled={sendingMessage || !newMessage.trim()}
            data-testid="send-message-btn"
          >
            {sendingMessage ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </Button>
        </form>

        <Button 
          variant="outline"
          className="w-full rounded-none" 
          onClick={onBack}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Contact Info
        </Button>
      </div>
    </>
  );
}
